// Числа, деньги, длительности и моменты для людей (задача D4). Экраны пульта берут форматы только отсюда.
//
// Набор (бриф D, «Тексты»):
// - разряды — узкий неразрывный пробел U+202F: «1 067 284»; дробная часть — через запятую;
// - перед «₽», «%», «с», «мин», «млн», «тыс.» — неразрывный пробел U+00A0; внутри момента и периода — тоже он:
//   «05.10 14:32 МСК» не разрывается строкой;
// - минус — «−» (U+2212); то, что округлилось до нуля, — без минуса;
// - между числами периода — «–» без пробелов; пустое значение — «—».
//
// Время — всегда по Москве с пометкой «МСК», в каком бы поясе ни был браузер: части даты берёт
// `Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow' }).formatToParts`, строка собирается вручную
// (вид `toLocaleString` зависит от версии ICU). Сервер отдаёт моменты ISO UTC.

/** Пустое значение. */
export const EMPTY = '—';

const NNBSP = '\u202F';
const NBSP = '\u00A0';
const MINUS = '\u2212';
const NDASH = '\u2013';

type NumberInput = number | null | undefined;
type MomentInput = string | Date | null | undefined;

// ───────────────────────────── числа ─────────────────────────────

/** Число без разрядов, с точкой и знаком только у отрицательных (−0 и то, что округлилось до нуля, — без знака). */
const plainFormats = new Map<number, Intl.NumberFormat>();

function plainFormat(digits: number): Intl.NumberFormat {
  let format = plainFormats.get(digits);
  if (format === undefined) {
    format = new Intl.NumberFormat('en-US', {
      useGrouping: false,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      signDisplay: 'negative',
    });
    plainFormats.set(digits, format);
  }
  return format;
}

/** Целая часть с разрядами через U+202F: «1067284» → «1 067 284». */
function groupThousands(integer: string): string {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
}

/** Число с `digits` знаками после запятой, разрядами и минусом «−»; нечисло — `null`. */
function number(n: NumberInput, digits: number): string | null {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  const plain = plainFormat(digits).format(n);
  const negative = plain.startsWith('-');
  const [integer = '0', fraction] = (negative ? plain.slice(1) : plain).split('.');
  const grouped = groupThousands(integer);
  return `${negative ? MINUS : ''}${fraction === undefined ? grouped : `${grouped},${fraction}`}`;
}

/** Целое с разрядами: `1067284` → «1 067 284». */
export function formatInt(n: NumberInput): string {
  return number(n, 0) ?? EMPTY;
}

/** Дробное с запятой и ровно `digits` знаками: `2.4` → «2,4». */
export function formatDecimal(n: NumberInput, digits: 1 | 2): string {
  return number(n, digits) ?? EMPTY;
}

/** Доля → проценты: `0.879` → «87,9 %». */
export function formatPercent(share: NumberInput, digits: 0 | 1 | 2 = 1): string {
  const value = share === null || share === undefined ? null : number(share * 100, digits);
  return value === null ? EMPTY : `${value}${NBSP}%`;
}

/** Рубли: «1 214 ₽», с копейками — «29,8 ₽», «0,61 ₽». */
export function formatRub(n: NumberInput, digits: 0 | 1 | 2 = 0): string {
  const value = number(n, digits);
  return value === null ? EMPTY : `${value}${NBSP}₽`;
}

/** Десятые доли единицы (`52` → «5,2»; `120` → «12»: «,0» отбрасывается). */
function tenths(count: number, negative: boolean): string {
  const whole = Math.trunc(count / 10);
  const rest = count % 10;
  const value = rest === 0 ? groupThousands(String(whole)) : `${groupThousands(String(whole))},${rest}`;
  return negative ? `${MINUS}${value}` : value;
}

/** Крупная сумма коротко: «≈ 5,2 млн ₽», «≈ 410 тыс. ₽», «≈ 870 ₽». */
export function formatRubShort(n: NumberInput): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return EMPTY;
  const abs = Math.abs(n);
  // Сначала округляем до десятых, потом выбираем единицу: 999 960 ₽ — «≈ 1 млн», а не «≈ 1 000 тыс.».
  const millions = Math.round(abs / 100_000);
  if (millions >= 10) return `≈${NBSP}${tenths(millions, n < 0)}${NBSP}млн${NBSP}₽`;
  const thousands = Math.round(abs / 100);
  if (thousands >= 10) return `≈${NBSP}${tenths(thousands, n < 0)}${NBSP}тыс.${NBSP}₽`;
  return `≈${NBSP}${formatInt(n)}${NBSP}₽`;
}

// ───────────────────────────── длительности ─────────────────────────────

/** Секунды: «47 с», «2,4 с»; от минуты — «4 мин 40 с», ровные минуты — «5 мин». */
export function formatSeconds(s: NumberInput, digits: 0 | 1 = 0): string {
  if (s === null || s === undefined || !Number.isFinite(s) || s < 0) return EMPTY;
  // Округляем до границы минуты: 59,6 с без знаков — это уже «1 мин», а не «60 с».
  const scale = 10 ** digits;
  if (Math.round(s * scale) / scale < 60) return `${number(s, digits) ?? EMPTY}${NBSP}с`;
  const total = Math.round(s);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  const head = `${formatInt(minutes)}${NBSP}мин`;
  return seconds === 0 ? head : `${head}${NBSP}${seconds}${NBSP}с`;
}

/** Часы-минуты-секунды как на часах: `42` → «0:42», `3725` → «1:02:05». */
export function formatClock(s: NumberInput): string {
  if (s === null || s === undefined || !Number.isFinite(s) || s < 0) return EMPTY;
  const total = Math.round(s);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

// ───────────────────────────── время по МСК ─────────────────────────────

const MSK_PARTS = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

interface MskParts {
  readonly year: string;
  readonly month: string;
  readonly day: string;
  readonly hour: string;
  readonly minute: string;
  readonly second: string;
}

/** Части момента по МСК; пустое или неверное значение — `null`. */
function mskParts(input: MomentInput): MskParts | null {
  if (input === null || input === undefined) return null;
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  const parts: Record<string, string> = {};
  for (const { type, value } of MSK_PARTS.formatToParts(date)) parts[type] = value;
  const { year, month, day, hour, minute, second } = parts;
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    second === undefined
  ) {
    return null;
  }
  return { year, month, day, hour, minute, second };
}

/** Календарный день по МСК: `'ГГГГ-ММ-ДД'` («сегодня» пульта — день по МСК). Неверный момент — `RangeError`. */
export function mskDay(iso: string | Date): string {
  const parts = mskParts(iso);
  if (parts === null) throw new RangeError(`mskDay: неверный момент «${String(iso)}»`);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Момент: «05.10 14:32 МСК»; год по МСК не тот, что сейчас, — «05.10.2025 14:32 МСК». */
export function formatMoment(iso: MomentInput, now: Date = new Date()): string {
  const parts = mskParts(iso);
  if (parts === null) return EMPTY;
  const sameYear = parts.year === mskParts(now)?.year;
  const day = sameYear ? `${parts.day}.${parts.month}` : `${parts.day}.${parts.month}.${parts.year}`;
  return `${day}${NBSP}${parts.hour}:${parts.minute}${NBSP}МСК`;
}

/** Время по МСК: «14:32», с секундами — «14:02:47». */
export function formatTime(iso: MomentInput, withSeconds = false): string {
  const parts = mskParts(iso);
  if (parts === null) return EMPTY;
  return withSeconds ? `${parts.hour}:${parts.minute}:${parts.second}` : `${parts.hour}:${parts.minute}`;
}

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** День «05.10»: из момента — по МСК, из строки дня `'ГГГГ-ММ-ДД'` — как есть, без пересчёта пояса. */
export function formatDay(isoOrDay: MomentInput): string {
  if (typeof isoOrDay === 'string') {
    const day = DAY_ONLY.exec(isoOrDay);
    if (day !== null) return `${day[3]}.${day[2]}`;
  }
  const parts = mskParts(isoOrDay);
  return parts === null ? EMPTY : `${parts.day}.${parts.month}`;
}

/** Период: в один день по МСК — «05.10 13:32–14:32 МСК», в разные — «29.09–05.10», в разные годы — с годами. */
export function formatPeriod(from: MomentInput, to: MomentInput): string {
  const start = mskParts(from);
  const end = mskParts(to);
  if (start === null || end === null) return EMPTY;
  if (start.year !== end.year) {
    return `${start.day}.${start.month}.${start.year}${NDASH}${end.day}.${end.month}.${end.year}`;
  }
  if (start.month === end.month && start.day === end.day) {
    return `${start.day}.${start.month}${NBSP}${start.hour}:${start.minute}${NDASH}${end.hour}:${end.minute}${NBSP}МСК`;
  }
  return `${start.day}.${start.month}${NDASH}${end.day}.${end.month}`;
}
